import { UserOutlined } from '@ant-design/icons'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import { User } from '@components/user/User'
import { Venue } from '@components/venue/Venue'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { Reservation } from '@models/Reservation'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getReservations,
  selectReservations,
  setReservationQuery,
} from '@store/ReservationReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import {
  initialQuery,
  SBTable,
  TableParams,
} from '@supplement-bacon/sb-table-filters'
import { currencyFormat } from '@utils/currencyFormatter'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import {
  Col,
  DatePicker,
  TableColumnsType,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import dayjs from 'dayjs'

const { Text } = Typography
const { RangePicker } = DatePicker

const TransactionsPage = () => {
  const dispatch = useAppDispatch()
  const reservations = useAppSelector(selectReservations())
  const { query, isLoading } = useAppSelector((state) => state.reservation)

  const activeFilters = (query.filters ?? {}) as Record<string, unknown>
  const hasActiveFilters = [
    'users',
    'venues',
    'startDate',
    'endDate',
    'status',
  ].some((key) => {
    const value = activeFilters[key]
    return Array.isArray(value) ? value.length > 0 : Boolean(value)
  })

  const columns: TableColumnsType<Reservation> = [
    {
      title: 'Venue',
      render: (record: Reservation) => <Venue.Avatar id={record.venue} />,
    },
    {
      title: 'Client',
      render: (record: Reservation) => <User.Avatar id={record.user} />,
    },
    {
      title: 'Party size',
      render: (record: Reservation) => (
        <Tag color="purple" icon={<UserOutlined />}>
          {record.guests}
        </Tag>
      ),
    },
    {
      title: 'Date',
      key: 'date',
      render: (record: Reservation) => (
        <Text style={{ whiteSpace: 'nowrap' }}>
          {dayjs(record.date).format('DD-MM-YYYY HH:mm')}
        </Text>
      ),
    },
    {
      title: 'Reference',
      render: (record: Reservation) => (
        <Tooltip title={record.reference}>
          <Tag variant="outlined" color="blue">
            <Text copyable={{ text: record.reference.toString() }}>
              {record.reference?.toString?.()}{' '}
            </Text>
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Seating area',
      render: (record: Reservation) => <Tag>{record.seatingArea?.en.name}</Tag>,
    },
    {
      title: 'Status',
      render: (record: Reservation) => (
        <Tag variant="outlined">{record.status}</Tag>
      ),
    },
    {
      title: 'Spend',
      render: (record: Reservation) =>
        record.financial ? (
          <Text>{currencyFormat(record.financial.amount, true)}</Text>
        ) : (
          '--'
        ),
    },
    {
      title: 'Source',
      render: (record: Reservation) => (
        <Tag variant="outlined" style={{ textTransform: 'capitalize' }}>
          {record.bookedByProduct}
        </Tag>
      ),
    },
  ]

  const fetchReservations = (params: TableUrlParams) => {
    // The reservation slice is shared with other pages (user fiche, business),
    // so start from a clean query instead of inheriting their filters.
    const newQuery: TableParams = {
      ...initialQuery,
      // Client and Seating area columns need these relations; both are
      // opt-in on the endpoint.
      with: ['user', 'seatingArea'],
      // This index only sorts by date descending.
      columnKey: 'date',
      order: 'descend',
      pagination: {
        ...initialQuery.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
      filters: params.filters,
      search: params.search,
    }
    dispatch(setReservationQuery(newQuery))
    dispatch(getReservations(newQuery))
  }

  const { handleFiltersChange, handleTableChange, params } =
    useTableUrlSync<Reservation>({ onParamsChange: fetchReservations })

  return (
    <Page
      permissions={Permission.listReservation}
      pageTitle={{
        title: 'Transactions',
        level: 4,
      }}
    >
      <Col span={24}>
        <TableFilters
          initialValues={{
            users: params.filters.users?.map(Number),
            venues: params.filters.venues?.map(Number),
            status: params.filters.status,
            dateRange:
              params.filters.startDate && params.filters.endDate
                ? [
                    dayjs(params.filters.startDate),
                    dayjs(params.filters.endDate),
                  ]
                : null,
          }}
          onValuesChange={(filters: any, search: any) => {
            const { dateRange, ...otherFilters } = filters

            const dateFilters =
              dateRange && dateRange[0] && dateRange[1]
                ? {
                    startDate: [dayjs(dateRange[0]).format('YYYY-MM-DD')],
                    endDate: [dayjs(dateRange[1]).format('YYYY-MM-DD')],
                  }
                : {}

            handleFiltersChange({ ...otherFilters, ...dateFilters }, search)
          }}
          left={[
            {
              name: 'users',
              type: 'filter',
              label: 'Clients',
              input: (
                <SBAsyncSelect
                  style={{ minWidth: '16ch' }}
                  multiple
                  selectProps={{ maxTagCount: 'responsive' }}
                  placeholder="Select clients"
                  entity={Entity.user}
                  searchFieldName="name"
                  allowClear
                />
              ),
            },
            {
              name: 'venues',
              type: 'filter',
              label: 'Venues',
              input: (
                <SBAsyncSelect
                  style={{ minWidth: '16ch' }}
                  multiple
                  selectProps={{
                    maxTagCount: 'responsive',
                  }}
                  placeholder="Select venues"
                  entity={Entity.venue}
                  searchFieldName="name"
                  allowClear
                />
              ),
            },
            {
              name: 'status',
              type: 'filter',
              label: 'Status',
              input: (
                <SBAsyncSelect
                  style={{ minWidth: '22ch' }}
                  multiple
                  selectProps={{ maxTagCount: 'responsive' }}
                  placeholder="Select status"
                  entity={Entity.reservationStatus}
                  allowClear
                />
              ),
            },
          ]}
          right={[
            {
              name: 'dateRange',
              type: 'filter',
              label: 'Date range',
              input: <RangePicker className="w-full" placement="bottomRight" />,
            },
          ]}
        />
      </Col>
      <Col span={24}>
        <SBTable
          columns={columns}
          dataSource={reservations}
          loading={isLoading}
          onChange={handleTableChange}
          pagination={query.pagination}
          scroll={{ x: 'max-content' }}
          rowKey="id"
          empty={{
            description: hasActiveFilters
              ? 'Aucun résultat pour ces filtres'
              : 'Aucune réservation',
          }}
        />
      </Col>
    </Page>
  )
}

export default TransactionsPage
